import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { caApi, errMsg, BTN_PRIMARY, BTN_OUTLINE } from './caShared';

const SUBS = {
  Skincare: ['Face Cleansing', 'Moisturizer', 'Serum', 'Sunscreen', 'Toner', 'Face Mask', 'Acne Care', 'Anti-Ageing'],
  Beauty: ['Makeup', 'Lipstick', 'Nail Care', 'Fragrance', 'Tools'],
  'Hair Care': ['Shampoo', 'Conditioner', 'Hair Oil', 'Hair Serum', 'Hair Colour'],
};
const DEFAULT_SUBS = ['Body Care', 'Bath & Shower', 'Natural', 'Vegan', 'Premium', 'Mass'];
const FIELD = 'w-full h-10 rounded-lg px-3 text-sm bg-white dark:bg-[#1A1D24] border border-[#D9DCE6] dark:border-white/10 focus:outline-none focus:border-[#3B4FD8] transition-colors';
const LABEL = 'text-xs font-semibold text-[#374151] dark:text-zinc-300 mb-1.5 block';
const EMPTY = { name: '', brand_id: '', instagram_handle: '', category: 'Skincare', sub_categories: [] };

export const CompetitorModal = ({ open, onOpenChange, editing, options, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(editing ? { ...EMPTY, ...editing, brand_id: editing.brand_id || '', instagram_handle: `@${editing.instagram_handle}` } : EMPTY); }, [open, editing]);
  const subs = [...new Set([...(SUBS[f.category] || DEFAULT_SUBS), ...f.sub_categories])];
  const toggle = (s) => setF({ ...f, sub_categories: f.sub_categories.includes(s) ? f.sub_categories.filter((x) => x !== s) : [...f.sub_categories, s] });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, brand_id: f.brand_id || null };
      const r = editing ? await caApi.put(`/${editing.id}`, body) : await caApi.post('', body);
      toast.success(editing ? 'Competitor updated' : `${r.name} added`, { description: editing ? undefined : 'Click Refresh to pull its data.' });
      onSaved(); onOpenChange(false);
    } catch (er) { toast.error(errMsg(er, 'Could not save competitor')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="competitor-modal">
        <DialogHeader><DialogTitle>{editing ? 'Edit Competitor' : 'Add Competitor'}</DialogTitle><DialogDescription>Track a competitor's Instagram activity and the creators they work with.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><label className={LABEL}>Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Minimalist" className={FIELD} data-testid="competitor-name-input" /></div>
          <div><label className={LABEL}>Brand</label>
            <select value={f.brand_id} onChange={(e) => setF({ ...f, brand_id: e.target.value })} className={FIELD} data-testid="competitor-brand-select">
              <option value="">Not linked to a brand</option>{(options?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
          <div><label className={LABEL}>Instagram Handle</label><input required value={f.instagram_handle} onChange={(e) => setF({ ...f, instagram_handle: e.target.value })} placeholder="@minimalist.india" className={FIELD} data-testid="competitor-handle-input" /></div>
          <div><label className={LABEL}>Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, sub_categories: [] })} className={FIELD} data-testid="competitor-category-select">
              {(options?.categories || ['Skincare']).map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><label className={LABEL}>Sub Category</label>
            <div className="flex flex-wrap gap-2" data-testid="competitor-subcategories">
              {subs.map((s) => {
                const on = f.sub_categories.includes(s);
                return <button type="button" key={s} onClick={() => toggle(s)} className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors inline-flex items-center gap-1 ${on ? 'bg-[#3B4FD8] border-[#3B4FD8] text-white' : 'border-[#D9DCE6] text-[#4B5563] hover:border-[#3B4FD8]/50'}`} data-testid={`competitor-sub-${s.replace(/\W+/g, '-').toLowerCase()}`}>{s}{on && <X size={11} />}</button>;
              })}
            </div></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className={BTN_OUTLINE} data-testid="competitor-cancel-btn">Cancel</button>
            <button disabled={busy} className={BTN_PRIMARY} data-testid="competitor-save-btn">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
